import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthenticatedLayout from './AuthenticatedLayout';

/**
 * AdminProtectedRoute - Protects admin routes with role-based access control.
 * 
 * Security behavior:
 * - If not authenticated: redirect to /login
 * - If authenticated but not admin: SILENT redirect to / (home)
 * - No "Access Denied" message - don't reveal admin pages exist
 */
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but not admin - silent redirect to products (their dashboard)
  // Don't reveal that admin pages exist
  if (user.role !== 'admin') {
    return <Navigate to="/products" replace />;
  }

  // Admin user - render the protected content
  return (
    <AuthenticatedLayout>
      {children}
    </AuthenticatedLayout>
  );
};

export default AdminProtectedRoute;
